// app/javascript/three/tools_drag_drop.js
//
// Drag & drop tools -> hotspots
// Versione pointer-based (NO HTML5 drag&drop) per evitare Snap/visualizzazione divisa di Windows
// + effetto: il tool sparisce dalla toolbox mentre lo trascini.
// Solo i tool con data-draggable="true" possono essere trascinati.

export function initToolsDragDrop({
  rootEl = document,
  toolSelector = "[data-tool-id]",
  hotspotSelector = ".hs-dot[data-hotspot-id]",
  draggingClass = "is-dragging-tool",
  hotspotHoverClass = "hs-drop-hover",
  hotspotActiveClass = "hs-drop-active",
  toolOriginHiddenClass = "tool-origin-hidden",
  onAssign = () => {},
} = {}) {
  let draggingToolId = null;
  let draggingToolEl = null;
  let ghostEl = null;
  let hoveredHotspotEl = null;
  let isDragging = false;

  function qsAll(sel) {
    return Array.from(rootEl.querySelectorAll(sel));
  }

  function isToolDraggable(toolEl) {
    return toolEl?.dataset?.draggable === "true";
  }

  function setHotspotsActive(active) {
    qsAll(hotspotSelector).forEach((hs) => {
      hs.classList.toggle(hotspotActiveClass, active);
      if (!active) hs.classList.remove(hotspotHoverClass);
    });
  }

  function clearHover() {
    if (hoveredHotspotEl) {
      hoveredHotspotEl.classList.remove(hotspotHoverClass);
      hoveredHotspotEl = null;
    }
  }

  function findHotspotUnderPointer(clientX, clientY) {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return null;
    return el.closest?.(hotspotSelector) || null;
  }

  function makeGhostFromTool(toolEl) {
    const g = document.createElement("div");
    g.className = "tool-ghost";
    g.style.position = "fixed";
    g.style.left = "0px";
    g.style.top = "0px";
    g.style.transform = "translate(-9999px, -9999px)";
    g.style.zIndex = "999999";
    g.style.pointerEvents = "none";
    g.style.userSelect = "none";
    g.style.width = "72px";
    g.style.height = "72px";

    const img = toolEl.querySelector("img");
    if (img) {
      const gi = document.createElement("img");
      gi.src = img.currentSrc || img.src;
      gi.alt = img.alt || "";
      gi.style.width = "100%";
      gi.style.height = "100%";
      gi.style.objectFit = "contain";
      gi.style.display = "block";
      gi.style.background = "rgba(255,255,255,0.9)";
      gi.style.borderRadius = "12px";
      gi.style.border = "1px solid rgba(0,0,0,0.08)";
      gi.style.boxShadow = "0 10px 30px rgba(0,0,0,0.25)";
      g.appendChild(gi);
    } else {
      g.textContent = "🔧";
      g.style.display = "flex";
      g.style.alignItems = "center";
      g.style.justifyContent = "center";
      g.style.fontSize = "28px";
      g.style.background = "rgba(255,255,255,0.9)";
      g.style.borderRadius = "12px";
      g.style.border = "1px solid rgba(0,0,0,0.08)";
      g.style.boxShadow = "0 10px 30px rgba(0,0,0,0.25)";
    }

    document.body.appendChild(g);
    return g;
  }

  function moveGhost(clientX, clientY) {
    if (!ghostEl) return;
    ghostEl.style.transform = `translate(${clientX - 36}px, ${clientY - 36}px)`;
  }

  function startDrag(e, toolEl) {
    draggingToolEl = toolEl;
    draggingToolId = toolEl.dataset.toolId;
    isDragging = true;

    document.body.classList.add(draggingClass);
    setHotspotsActive(true);

    ghostEl = makeGhostFromTool(toolEl);
    moveGhost(e.clientX, e.clientY);

    requestAnimationFrame(() => {
      toolEl.classList.add(toolOriginHiddenClass);
    });
  }

  function endDrag({ cancelled = false } = {}) {
    if (draggingToolEl) draggingToolEl.classList.remove(toolOriginHiddenClass);

    document.body.classList.remove(draggingClass);
    setHotspotsActive(false);
    clearHover();

    if (ghostEl) {
      ghostEl.remove();
      ghostEl = null;
    }

    draggingToolEl = null;
    draggingToolId = null;
    isDragging = false;

    if (cancelled) return;
  }

  function handlePointerDown(e) {
    if (e.button != null && e.button !== 0) return;

    const toolEl = e.target.closest?.(toolSelector);
    if (!toolEl) return;

    if (!isToolDraggable(toolEl)) {
      return;
    }

    e.preventDefault();
    startDrag(e, toolEl);

    try {
      toolEl.setPointerCapture?.(e.pointerId);
    } catch (_) {}
  }

  function handlePointerMove(e) {
    if (!isDragging) return;

    e.preventDefault();
    moveGhost(e.clientX, e.clientY);

    const hs = findHotspotUnderPointer(e.clientX, e.clientY);

    if (hoveredHotspotEl && hoveredHotspotEl !== hs) {
      hoveredHotspotEl.classList.remove(hotspotHoverClass);
    }

    hoveredHotspotEl = hs || null;

    if (ghostEl) {
      ghostEl.classList.remove("is-over-hotspot");
    }

    if (hs) {
      hs.classList.add(hotspotHoverClass);
      ghostEl?.classList.add("is-over-hotspot");
    }
  }

  function handlePointerUp(e) {
    if (!isDragging) return;

    e.preventDefault();

    const hs = findHotspotUnderPointer(e.clientX, e.clientY);
    const toolId = draggingToolId;
    const hotspotId = hs?.dataset?.hotspotId;

    endDrag();

    if (hs && toolId && hotspotId) {
      onAssign({ toolId, hotspotId, hotspotEl: hs });
    }
  }

  function handlePointerCancel() {
    if (!isDragging) return;
    endDrag({ cancelled: true });
  }

  function disableNativeDnD() {
    qsAll(toolSelector).forEach((el) => {
      el.setAttribute("draggable", "false");
      const img = el.querySelector("img");
      if (img) img.setAttribute("draggable", "false");
    });
  }

  function preventNativeDragStart(e) {
    const toolEl = e.target.closest?.(toolSelector);
    if (!toolEl) return;
    e.preventDefault();
  }

  disableNativeDnD();

  rootEl.addEventListener("pointerdown", handlePointerDown);
  window.addEventListener("pointermove", handlePointerMove, { passive: false });
  window.addEventListener("pointerup", handlePointerUp, { passive: false });
  window.addEventListener("pointercancel", handlePointerCancel);

  rootEl.addEventListener("dragstart", preventNativeDragStart);

  return {
    destroy() {
      rootEl.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);

      rootEl.removeEventListener("dragstart", preventNativeDragStart);

      endDrag({ cancelled: true });
    },
  };
}